import React from 'react';
import { ReportSectionLimitation } from '@/types';

interface Props {
  limitations: ReportSectionLimitation[];
}

/**
 * Section-level "Areas Not Fully Inspected" callout. Renders above the
 * component blocks in the portal so scope exclusions are visible before
 * the reader starts skimming findings. Amber + alert-icon matches the
 * template editor's limitation row styling for consistency.
 *
 * Mirrors Spectora's competitive pattern — see
 * docs/research/competitive-report-analysis.md.
 */
const SectionLimitationsBlock: React.FC<Props> = ({ limitations }) => {
  if (limitations.length === 0) return null;

  return (
    <section
      className="rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3"
      aria-label="Areas not fully inspected"
    >
      <div className="flex items-center gap-2 text-amber-900">
        <svg
          className="h-4 w-4 shrink-0"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M8.485 2.495a1.75 1.75 0 013.03 0l6.28 10.86A1.75 1.75 0 0116.28 16H3.72a1.75 1.75 0 01-1.515-2.645l6.28-10.86zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          />
        </svg>
        <h3 className="text-body-sm font-semibold uppercase tracking-wide">
          Areas not fully inspected
        </h3>
      </div>
      <ul className="mt-2 space-y-1.5 text-body-sm text-ink">
        {limitations.map((lim, i) => (
          <li key={i} className="leading-relaxed">
            <span className="font-medium text-ink">{lim.componentName}:</span>{' '}
            <span className="whitespace-pre-wrap">{lim.body}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default SectionLimitationsBlock;
